import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { capitalize } from 'lodash'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import type { RProviders } from 'v2/pages/flow/sidebar/types'
import { VoicePlayControl } from '../../components/voice-play-control'
import { generateAudioSchema, invalidSelectValueSchema } from '../../config/validation.config'
import type { AiSkillsNodeData, VoicePreviewHandler } from '../../types'
import { SkillInstructionsTextArea } from '../../utils/skill-instructions-textarea'

type FormValues = AiSkillsNodeData['config']

interface AudioHandlers {
  isPlaying: (voiceId: string) => boolean
  handlePlayPreview: VoicePreviewHandler
  loadingVoiceId: string | null
  stop: () => void
}

export const getTextToSpeechFields = (
  nodeVars: string[],
  providers?: RProviders,
  audioHandlers?: AudioHandlers,
): GenericFieldConfig<FormValues> => [
  {
    name: 'text_to_speech_config.model_id',
    componentType: 'selectWithGroups',
    onChangeListenTo: ['tool_type'],
    zodValidation: invalidSelectValueSchema(
      generateAudioSchema.shape.model_id,
      providers?.tts_providers?.flatMap((provider) => provider.models.map((model) => model.id)) ??
        [],
    ),
    eventDataExtractor: (value) => value,
    afterOnChange: (value, _, form) => {
      if (!form) return
      const provider = providers?.tts_providers.find((p) =>
        p.models.some((model) => model.id === value.text_to_speech_config?.model_id),
      )
      form.setFieldValue('text_to_speech_config.provider_id', provider?.id ?? '')
      form.setFieldValue('text_to_speech_config.voice_id', provider?.voices?.[0]?.id ?? '')
      form.validateAllFields('change')
    },
    inputProps: {
      placeholder: 'Select model',
      label: 'Model',
      groups: providers?.tts_providers.map((provider) => ({
        label: provider.label,
        options: provider.models.map((model) => ({
          label: (
            <div className="flex items-center gap-2">
              <img src={provider.meta.icon_url} alt={provider.label} className="w-4 h-4" />
              {capitalize(`${model.label}`)}
            </div>
          ),
          searchValue: `${provider.label} ${model.label}`,
          value: model.id,
        })),
      })),
    },
    visible: (state) => state.tool_type === 'text_to_speech',
  },
  {
    name: 'text_to_speech_config.voice_id',
    componentType: 'select',
    label: 'Voice',
    onChangeListenTo: ['text_to_speech_config.provider_id', 'tool_type'],
    zodValidation: invalidSelectValueSchema(
      generateAudioSchema.shape.voice_id,
      providers?.tts_providers?.flatMap(
        (provider) => provider.voices?.map((voice) => voice.id) ?? [],
      ) ?? [],
    ),
    getDynamicProps: (value) => {
      const voiceFromProvider = providers?.tts_providers.find(
        (provider) => provider.id === value.text_to_speech_config?.provider_id,
      )?.voices
      return {
        inputProps: {
          placeholder: 'Select voice',
          options: voiceFromProvider?.map((voice) => ({
            label: voice.label,
            value: voice.id,
            icon: audioHandlers ? (
              <VoicePlayControl
                voice={voice}
                sttTtsConfig={{
                  ttsModel: value.text_to_speech_config?.model_id || '',
                  ttsProvider: value.text_to_speech_config?.provider_id || '',
                }}
                isPlaying={audioHandlers.isPlaying}
                loadingVoiceId={audioHandlers.loadingVoiceId}
                onPlayPreview={(voiceId, previewUrl, model, provider, languageCode) =>
                  audioHandlers.handlePlayPreview({
                    voiceId,
                    previewUrl,
                    model: model || value.text_to_speech_config?.model_id,
                    provider: provider || value.text_to_speech_config?.provider_id,
                    languageCode,
                  })
                }
                onStop={audioHandlers.stop}
              />
            ) : null,
          })),
        },
      }
    },
    visible: (state) => state.tool_type === 'text_to_speech',
  },
  {
    name: 'text_to_speech_config.text',
    componentType: 'textarea',
    onChangeListenTo: ['tool_type'],
    zodValidation: generateAudioSchema.shape.text,
    visible: (state) => state.tool_type === 'text_to_speech',
    inputProps: {
      placeholder: 'Enter text',
      label: 'Text',
      endIconClassName: 'pr-2',
      endIcon: (
        <NodevarList nodevars={nodeVars} inputElementSelector="#text_to_speech_config.text" />
      ),
    },
  },
  {
    name: 'text_to_speech_config.instructions',
    type: 'custom',
    label: 'Additional Instructions',
    onChangeListenTo: ['tool_type'],
    component: SkillInstructionsTextArea,
    eventDataExtractor: (e) => e,
    zodValidation: generateAudioSchema.shape.instructions,
    inputProps: {
      placeholder:
        'ex: Create a friendly welcome message for new customers in a warm, conversational tone',
      rows: 4,
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          inputElementSelector="#text_to_speech_config.instructions .ql-ref"
        />
      ),
      'data-testid': 'textarea-skill-instructions',
    },
    visible: (state) =>
      state.tool_type === 'text_to_speech' &&
      !(
        state.text_to_speech_config?.provider_id === 'cartesia' ||
        state.text_to_speech_config?.provider_id === 'elevenlabs'
      ),
  },
]
